
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Calendar, Award, HelpCircle, Wind } from 'lucide-react';
import { Link } from 'react-router-dom';

const ResourcesPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-brand-charcoal-gray to-brand-charcoal-gray/80 text-white py-20 md:py-28 overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/5f3827e5-0c99-43d2-80dc-04b1857cd807.png')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
        
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold drop-shadow-lg">
              Roofing Resources
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto drop-shadow-md">
              Helpful information to guide you through your roofing project
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card>
              <CardHeader>
                <FileText className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Roofing Guides</CardTitle>
                <CardDescription>
                  Comprehensive guides to help you understand roofing materials and processes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/understanding-roofing-materials">
                        Understanding Roofing Materials
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/roof-replacement-process">
                        The Roof Replacement Process
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/identify-roof-damage">
                        How to Identify Roof Damage
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/roof-ventilation-guide">
                        <Wind className="inline-block w-4 h-4 mr-2" />
                        Complete Roof Ventilation Guide
                      </Link>
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <Calendar className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Insurance Information</CardTitle>
                <CardDescription>
                  Resources to help navigate insurance claims for roof damage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/insurance-claim-process">
                        The Insurance Claim Process
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/working-with-adjusters">
                        Working with Insurance Adjusters
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/documentation-requirements">
                        Documentation Requirements
                      </Link>
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <Award className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Warranty Information</CardTitle>
                <CardDescription>
                  Details about our warranties and manufacturer guarantees
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/workmanship-warranty">
                        Our Workmanship Warranty
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/manufacturer-warranties">
                        Manufacturer Warranties Explained
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/warranty-registration">
                        Warranty Registration Process
                      </Link>
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <HelpCircle className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>FAQ</CardTitle>
                <CardDescription>
                  Answers to commonly asked questions about roofing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/general-roofing-faq">
                        General Roofing FAQ
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/roofing-materials-faq">
                        Roofing Materials FAQ
                      </Link>
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-left" asChild>
                      <Link to="/resources/roofing-costs-faq">
                        Roofing Costs FAQ
                      </Link>
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          <div 
            className="relative text-center py-16 bg-brand-charcoal-gray text-white rounded-lg overflow-hidden"
            style={{
              backgroundImage: `url('/lovable-uploads/05712f55-6608-4d58-b71e-fc4dd31ec82a.png')`,
              backgroundSize: '200px 200px',
              backgroundRepeat: 'repeat',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
            
            <div className="relative z-10">
              <p className="mb-6 text-xl">
                Need more specific information or have questions not covered here?
              </p>
              <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold" asChild>
                <Link to="/contact">Contact Our Team</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResourcesPage;
